/**
 * Templates Page Component
 * Dev-only page for previewing and testing component templates
 * Access: Dev mode only + Global admin role required
 */

import { useState } from 'react';
import {
  Text,
  makeStyles,
  tokens,
  shorthands,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  MessageBarIntent,
} from '@fluentui/react-components';
import { Alert } from '../../components/common/Alert';
import {
  Edit20Regular,
  Delete20Regular,
  Shield20Regular,
  ArrowClockwise20Regular,
  Add20Regular,
  ErrorCircle16Filled,
  CheckmarkCircle16Filled,
  Warning16Filled,
  Info16Filled,
} from '@fluentui/react-icons';
import { AppPageHeader } from '../../components/templates'; // Used by PageHeaderDemos
import {
  PageLayout,
  DataGridCard,
  DataGridToolbar,
  ToolbarButton,
  ToolbarSearch,
  createTextColumn,
  createStatusDotColumn,
  createDateColumn,
  createColumnWithRowActions,
} from '../../components/layouts';

const useDemoStyles = makeStyles({
  demoSection: {
    marginBottom: tokens.spacingVerticalXXL,
  },
  demoHeader: {
    marginBottom: tokens.spacingVerticalM,
  },
  demoTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: tokens.spacingVerticalXS,
  },
  demoDescription: {
    color: tokens.colorNeutralForeground3,
  },
});

/**
 * DataGrid Demo - Uses layout components for consistent styling
 */

interface TeamMember {
  id: number;
  name: string;
  email: string;
  initials: string;
  colorIndex: number;
  role: string;
  department: string;
  status: 'active' | 'pending' | 'inactive';
  lastActive: Date;
}

const teamMembers: TeamMember[] = [
  {
    id: 1,
    name: 'Sarah Mitchell',
    email: 'sarah.mitchell@firm.com',
    initials: 'SM',
    colorIndex: 0,
    role: 'Partner',
    department: 'Litigation',
    status: 'active',
    lastActive: new Date(Date.now() - 5 * 60 * 1000),
  },
  {
    id: 2,
    name: 'James Chen',
    email: 'james.chen@firm.com',
    initials: 'JC',
    colorIndex: 1,
    role: 'Associate',
    department: 'Corporate',
    status: 'active',
    lastActive: new Date(Date.now() - 2 * 60 * 60 * 1000),
  },
  {
    id: 3,
    name: 'Emily Rodriguez',
    email: 'emily.rodriguez@firm.com',
    initials: 'ER',
    colorIndex: 2,
    role: 'Paralegal',
    department: 'Litigation',
    status: 'pending',
    lastActive: new Date(Date.now() - 24 * 60 * 60 * 1000),
  },
  {
    id: 4,
    name: 'Michael Thompson',
    email: 'michael.thompson@firm.com',
    initials: 'MT',
    colorIndex: 3,
    role: 'Partner',
    department: 'Real Estate',
    status: 'active',
    lastActive: new Date(Date.now() - 30 * 60 * 1000),
  },
  {
    id: 5,
    name: 'Amanda Foster',
    email: 'amanda.foster@firm.com',
    initials: 'AF',
    colorIndex: 0,
    role: 'Associate',
    department: 'Corporate',
    status: 'inactive',
    lastActive: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
  },
  {
    id: 6,
    name: 'David Park',
    email: 'david.park@firm.com',
    initials: 'DP',
    colorIndex: 1,
    role: 'IT Administrator',
    department: 'Operations',
    status: 'active',
    lastActive: new Date(Date.now() - 10 * 60 * 1000),
  },
];

// Column definitions using factory functions
const teamColumns = [
  createColumnWithRowActions<TeamMember>({
    columnId: 'name',
    header: 'Name',
    compare: (a, b) => a.name.localeCompare(b.name),
    renderContent: (item) => (
      <span title={item.name} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {item.name}
      </span>
    ),
    getActions: () => [
      { label: 'Edit User', icon: <Edit20Regular />, onClick: () => {} },
      { label: 'Manage Access', icon: <Shield20Regular />, onClick: () => {} },
      { label: 'Remove', icon: <Delete20Regular />, onClick: () => {} },
    ],
    getAriaLabel: (item) => `Actions for ${item.name}`,
  }),
  createTextColumn<TeamMember>({
    columnId: 'role',
    header: 'Role',
    getValue: (item) => item.role,
  }),
  createTextColumn<TeamMember>({
    columnId: 'department',
    header: 'Department',
    getValue: (item) => item.department,
  }),
  createStatusDotColumn<TeamMember>({
    columnId: 'status',
    header: 'Status',
    getStatus: (item) => ({
      label: item.status.charAt(0).toUpperCase() + item.status.slice(1),
      type: item.status,
    }),
  }),
  createDateColumn<TeamMember>({
    columnId: 'lastActive',
    header: 'Last Active',
    getDate: (item) => item.lastActive,
    format: 'relative',
  }),
];

function DataGridDemos() {
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [searchValue, setSearchValue] = useState('');

  return (
    <DataGridCard
      items={teamMembers}
      columns={teamColumns}
      getRowId={(item) => item.id}
      toolbar={
        <DataGridToolbar>
          <DataGridToolbar.Left>
            <ToolbarButton icon={<ArrowClockwise20Regular />} onClick={() => {}}>
              Refresh
            </ToolbarButton>
          </DataGridToolbar.Left>
          <DataGridToolbar.Right>
            <ToolbarSearch
              value={searchValue}
              onChange={setSearchValue}
              placeholder="Search..."
            />
            {selectedIds.size > 0 && (
              <ToolbarButton icon={<Delete20Regular />} onClick={() => {}}>
                Delete ({selectedIds.size})
              </ToolbarButton>
            )}
            <ToolbarButton appearance="primary" icon={<Add20Regular />} onClick={() => {}}>
              Add User
            </ToolbarButton>
          </DataGridToolbar.Right>
        </DataGridToolbar>
      }
      pagination={{
        showing: teamMembers.length,
        total: 47,
        page: 1,
        totalPages: 8,
      }}
      selectable
      selectedIds={selectedIds}
      onSelectionChange={setSelectedIds}
    />
  );
}

// Token debug panel styles
const useTokenStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('24px'),
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('12px'),
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    paddingBottom: '8px',
  },
  tokenGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    ...shorthands.gap('8px'),
  },
  tokenItem: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
    ...shorthands.padding('8px', '12px'),
    ...shorthands.borderRadius('6px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
  },
  colorSwatch: {
    width: '40px',
    height: '40px',
    ...shorthands.borderRadius('6px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    flexShrink: 0,
  },
  tokenInfo: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('2px'),
    minWidth: 0,
  },
  tokenName: {
    fontSize: '12px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    fontFamily: 'monospace',
    wordBreak: 'break-all',
  },
  tokenValue: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    fontFamily: 'monospace',
  },
});

// Alert demo styles
const useAlertDemoStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('32px'),
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    ...shorthands.gap('24px'),
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
    minWidth: 0,
    ...shorthands.overflow('hidden'),
  },
  columnHeader: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    paddingBottom: tokens.spacingVerticalS,
  },
  intentLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  alertSection: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
  },
});

// Improved Alert Preview styles - matching Fluent UI MessageBar
const useImprovedAlertStyles = makeStyles({
  alert: {
    display: 'flex',
    alignItems: 'flex-start',
    ...shorthands.padding('8px', '12px'),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.gap('8px'),
    minHeight: '36px',
    color: tokens.colorNeutralForeground1,  // Dark text like MessageBar
  },
  alertError: {
    backgroundColor: tokens.colorStatusDangerBackground1,
  },
  alertSuccess: {
    backgroundColor: tokens.colorStatusSuccessBackground1,
  },
  alertWarning: {
    backgroundColor: tokens.colorStatusWarningBackground1,
  },
  alertInfo: {
    backgroundColor: tokens.colorNeutralBackground1Hover,
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    paddingTop: '2px',
  },
  iconError: {
    color: tokens.colorStatusDangerForeground1,
  },
  iconSuccess: {
    color: tokens.colorStatusSuccessForeground1,
  },
  iconWarning: {
    color: tokens.colorStatusWarningForeground1,
  },
  iconInfo: {
    color: tokens.colorNeutralForeground2,
  },
  content: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    flexGrow: 1,
    minWidth: 0,
  },
  title: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    marginRight: '4px',
  },
  body: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
});

interface ImprovedAlertPreviewProps {
  intent: 'error' | 'success' | 'warning' | 'info';
  title?: string;
  children: React.ReactNode;
}

function ImprovedAlertPreview({ intent, title, children }: ImprovedAlertPreviewProps) {
  const styles = useImprovedAlertStyles();

  const intentClass = {
    error: styles.alertError,
    success: styles.alertSuccess,
    warning: styles.alertWarning,
    info: styles.alertInfo,
  }[intent];

  const iconClass = {
    error: styles.iconError,
    success: styles.iconSuccess,
    warning: styles.iconWarning,
    info: styles.iconInfo,
  }[intent];

  const IconComponent = {
    error: ErrorCircle16Filled,
    success: CheckmarkCircle16Filled,
    warning: Warning16Filled,
    info: Info16Filled,
  }[intent];

  return (
    <div role="alert" className={`${styles.alert} ${intentClass}`}>
      <span className={`${styles.icon} ${iconClass}`}>
        <IconComponent />
      </span>
      <div className={styles.content}>
        {title && <span className={styles.title}>{title} </span>}
        <span className={styles.body}>{children}</span>
      </div>
    </div>
  );
}

function AlertsDemo() {
  const styles = useAlertDemoStyles();

  const intents: Array<{ intent: 'error' | 'success' | 'warning' | 'info'; label: string }> = [
    { intent: 'error', label: 'Error' },
    { intent: 'success', label: 'Success' },
    { intent: 'warning', label: 'Warning' },
    { intent: 'info', label: 'Info' },
  ];

  const messageBarIntentMap: Record<string, MessageBarIntent> = {
    error: 'error',
    success: 'success',
    warning: 'warning',
    info: 'info',
  };

  const longMessage = 'This is a much longer message to test how the component handles text wrapping. When users encounter errors or important notifications, the message text can sometimes be quite lengthy and needs to wrap properly across multiple lines without breaking the layout or hiding important information.';

  return (
    <div className={styles.container}>
      <Text style={{ color: tokens.colorNeutralForeground2, marginBottom: '8px' }}>
        Compare the current Alert, the proposed improved Alert, and Fluent UI MessageBar.
        The improved Alert adds icons and horizontal layout to match MessageBar while avoiding ResizeObserver.
      </Text>

      <div className={styles.row}>
        {/* Current Alert Column */}
        <div className={styles.column}>
          <Text className={styles.columnHeader}>Current Alert</Text>
          {intents.map(({ intent, label }) => (
            <div key={`alert-${intent}`} className={styles.alertSection}>
              <Text className={styles.intentLabel}>{label}</Text>
              <Alert intent={intent} title={`${label} Title`}>
                {longMessage}
              </Alert>
            </div>
          ))}
          <div className={styles.alertSection}>
            <Text className={styles.intentLabel}>Without Title</Text>
            <Alert intent="info">
              {longMessage}
            </Alert>
          </div>
        </div>

        {/* Improved Alert Preview Column */}
        <div className={styles.column}>
          <Text className={styles.columnHeader}>Proposed Improved Alert</Text>
          {intents.map(({ intent, label }) => (
            <div key={`improved-${intent}`} className={styles.alertSection}>
              <Text className={styles.intentLabel}>{label}</Text>
              <ImprovedAlertPreview intent={intent} title={`${label} Title`}>
                {longMessage}
              </ImprovedAlertPreview>
            </div>
          ))}
          <div className={styles.alertSection}>
            <Text className={styles.intentLabel}>Without Title</Text>
            <ImprovedAlertPreview intent="info">
              {longMessage}
            </ImprovedAlertPreview>
          </div>
        </div>

        {/* Fluent UI MessageBar Column */}
        <div className={styles.column}>
          <Text className={styles.columnHeader}>Fluent UI MessageBar</Text>
          {intents.map(({ intent, label }) => (
            <div key={`messagebar-${intent}`} className={styles.alertSection}>
              <Text className={styles.intentLabel}>{label}</Text>
              <MessageBar intent={messageBarIntentMap[intent]}>
                <MessageBarBody>
                  <MessageBarTitle>{label} Title</MessageBarTitle>
                  {longMessage}
                </MessageBarBody>
              </MessageBar>
            </div>
          ))}
          <div className={styles.alertSection}>
            <Text className={styles.intentLabel}>Without Title</Text>
            <MessageBar intent="info">
              <MessageBarBody>
                {longMessage}
              </MessageBarBody>
            </MessageBar>
          </div>
        </div>
      </div>
    </div>
  );
}

function TokensDemo() {
  const styles = useTokenStyles();

  // Background tokens
  const backgroundTokens = [
    { name: 'colorNeutralBackground1', value: tokens.colorNeutralBackground1 },
    { name: 'colorNeutralBackground2', value: tokens.colorNeutralBackground2 },
    { name: 'colorNeutralBackground3', value: tokens.colorNeutralBackground3 },
    { name: 'colorNeutralBackground4', value: tokens.colorNeutralBackground4 },
    { name: 'colorNeutralBackground5', value: tokens.colorNeutralBackground5 },
    { name: 'colorNeutralBackground6', value: tokens.colorNeutralBackground6 },
    { name: 'colorNeutralBackground1Hover', value: tokens.colorNeutralBackground1Hover },
    { name: 'colorNeutralBackground1Pressed', value: tokens.colorNeutralBackground1Pressed },
    { name: 'colorNeutralBackground1Selected', value: tokens.colorNeutralBackground1Selected },
    { name: 'colorSubtleBackground', value: tokens.colorSubtleBackground },
    { name: 'colorSubtleBackgroundHover', value: tokens.colorSubtleBackgroundHover },
    { name: 'colorSubtleBackgroundPressed', value: tokens.colorSubtleBackgroundPressed },
    { name: 'colorSubtleBackgroundSelected', value: tokens.colorSubtleBackgroundSelected },
  ];

  // Foreground tokens
  const foregroundTokens = [
    { name: 'colorNeutralForeground1', value: tokens.colorNeutralForeground1 },
    { name: 'colorNeutralForeground2', value: tokens.colorNeutralForeground2 },
    { name: 'colorNeutralForeground3', value: tokens.colorNeutralForeground3 },
    { name: 'colorNeutralForeground4', value: tokens.colorNeutralForeground4 },
    { name: 'colorNeutralForegroundDisabled', value: tokens.colorNeutralForegroundDisabled },
  ];

  // Stroke/Border tokens
  const strokeTokens = [
    { name: 'colorNeutralStroke1', value: tokens.colorNeutralStroke1 },
    { name: 'colorNeutralStroke2', value: tokens.colorNeutralStroke2 },
    { name: 'colorNeutralStroke3', value: tokens.colorNeutralStroke3 },
    { name: 'colorNeutralStrokeSubtle', value: tokens.colorNeutralStrokeSubtle },
    { name: 'colorNeutralStrokeAccessible', value: tokens.colorNeutralStrokeAccessible },
  ];

  // Brand tokens
  const brandTokens = [
    { name: 'colorBrandBackground', value: tokens.colorBrandBackground },
    { name: 'colorBrandBackgroundHover', value: tokens.colorBrandBackgroundHover },
    { name: 'colorBrandBackgroundPressed', value: tokens.colorBrandBackgroundPressed },
    { name: 'colorBrandBackgroundSelected', value: tokens.colorBrandBackgroundSelected },
    { name: 'colorBrandBackground2', value: tokens.colorBrandBackground2 },
    { name: 'colorBrandForeground1', value: tokens.colorBrandForeground1 },
    { name: 'colorBrandForeground2', value: tokens.colorBrandForeground2 },
    { name: 'colorBrandStroke1', value: tokens.colorBrandStroke1 },
    { name: 'colorBrandStroke2', value: tokens.colorBrandStroke2 },
  ];

  // Status tokens
  const statusTokens = [
    { name: 'colorStatusSuccessBackground1', value: tokens.colorStatusSuccessBackground1 },
    { name: 'colorStatusSuccessForeground1', value: tokens.colorStatusSuccessForeground1 },
    { name: 'colorStatusWarningBackground1', value: tokens.colorStatusWarningBackground1 },
    { name: 'colorStatusWarningForeground1', value: tokens.colorStatusWarningForeground1 },
    { name: 'colorStatusDangerBackground1', value: tokens.colorStatusDangerBackground1 },
    { name: 'colorStatusDangerForeground1', value: tokens.colorStatusDangerForeground1 },
  ];

  const renderTokenSection = (title: string, tokenList: Array<{ name: string; value: string }>) => (
    <div className={styles.section}>
      <Text className={styles.sectionTitle}>{title}</Text>
      <div className={styles.tokenGrid}>
        {tokenList.map((token) => (
          <div key={token.name} className={styles.tokenItem}>
            <div
              className={styles.colorSwatch}
              style={{ backgroundColor: token.value }}
            />
            <div className={styles.tokenInfo}>
              <span className={styles.tokenName}>{token.name}</span>
              <span className={styles.tokenValue}>{token.value}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      <Text style={{ color: tokens.colorNeutralForeground2, marginBottom: '8px' }}>
        Toggle dark mode (top right) to see how these tokens change automatically.
      </Text>
      {renderTokenSection('Background Tokens', backgroundTokens)}
      {renderTokenSection('Foreground Tokens', foregroundTokens)}
      {renderTokenSection('Stroke/Border Tokens', strokeTokens)}
      {renderTokenSection('Brand Tokens', brandTokens)}
      {renderTokenSection('Status Tokens', statusTokens)}
    </div>
  );
}

export function TemplatesPage() {
  const [selectedTab, setSelectedTab] = useState<string>('datagrid');

  return (
    <PageLayout
      title="Component Templates"
      tabs={[
        { value: 'datagrid', label: 'DataGrid' },
        { value: 'pageheader', label: 'Page Header' },
        { value: 'tokens', label: 'Fluent Tokens' },
        { value: 'alerts', label: 'Alerts' },
      ]}
      selectedTab={selectedTab}
      onTabChange={setSelectedTab}
      testId="templates-page"
    >
      {selectedTab === 'datagrid' && <DataGridDemos />}
      {selectedTab === 'pageheader' && <PageHeaderDemos />}
      {selectedTab === 'tokens' && <TokensDemo />}
      {selectedTab === 'alerts' && <AlertsDemo />}
    </PageLayout>
  );
}

function PageHeaderDemos() {
  const demoStyles = useDemoStyles();
  const [demoTab, setDemoTab] = useState('tab1');

  return (
    <>
      {/* With Tabs */}
      <div className={demoStyles.demoSection}>
        <div className={demoStyles.demoHeader}>
          <Text className={demoStyles.demoTitle} block>Page Header with Tabs</Text>
          <Text className={demoStyles.demoDescription}>
            Standard page header with title, subtitle, and tab navigation.
          </Text>
        </div>
        <AppPageHeader
          title="User Management"
          subtitle="Manage users and their permissions in your organization"
          tabs={[
            { value: 'tab1', label: 'Users' },
            { value: 'tab2', label: 'Groups' },
            { value: 'tab3', label: 'Permissions' },
          ]}
          selectedTab={demoTab}
          onTabChange={setDemoTab}
        />
      </div>

      {/* Without Tabs */}
      <div className={demoStyles.demoSection}>
        <div className={demoStyles.demoHeader}>
          <Text className={demoStyles.demoTitle} block>Page Header without Tabs</Text>
          <Text className={demoStyles.demoDescription}>
            Simple header with just title and subtitle.
          </Text>
        </div>
        <AppPageHeader
          title="Dashboard"
          subtitle="Overview of your organization's activity"
        />
      </div>

      {/* Title Only */}
      <div className={demoStyles.demoSection}>
        <div className={demoStyles.demoHeader}>
          <Text className={demoStyles.demoTitle} block>Page Header - Title Only</Text>
          <Text className={demoStyles.demoDescription}>
            Minimal header with just the page title.
          </Text>
        </div>
        <AppPageHeader
          title="Settings"
        />
      </div>
    </>
  );
}
